/**
 * Central place for dashboard metric card → in-app list URLs (embedded: always `shop` + `host`).
 *
 * ---
 * Parity checklist (dashboard revenue cards vs live API, after summaries are fresh):
 *
 * 1. Ensure `shop_daily_metrics` covers the dashboard period (e.g. `refresh_analytics` completed for that range).
 * 2. Use the same YYYY-MM-DD strings as the dashboard "From" / "To" as `createdAtMin` and `createdAtMax`.
 * 3. For each segment, GET (authenticated) `/api/analytics/revenue-details?shop=…&host=…&page=1&createdAtMin=…&createdAtMax=…&revenueSegment=…`
 *    with `revenueSegment` one of: `total_revenue_all`, `revenue_fulfilled`, `revenue_delivered`.
 * 4. Compare response `totalRevenue` to the matching dashboard card (Total Revenue (All) / Fulfilled / Delivered).
 * 5. Optional: compare `matchingOrderCount` to the corresponding orders-segment count where it applies.
 * 6. If totals differ, check stale cache vs live orders first; then courier-status normalization edge cases (see `dashboardDrilldownWhere`).
 */
import type { DashboardOrderSegment } from '@/lib/orders/dashboardDrilldownWhere'
import type { RevenueDrilldownSegment } from '@/lib/orders/revenueDrilldownSegment'
import type { MetricBreakdownKey } from '@/lib/dashboard/metricBreakdownKeys'

/** Expense bucket stored on rows; matches dashboard ads/operational rollup keys. */
export type ExpenseDrilldownCategory = 'ADS' | 'OPERATIONAL'

function appendShopHost(base: URLSearchParams, shop: string, host: string | null | undefined): void {
  base.set('shop', shop)
  if (host) base.set('host', host)
}

/**
 * Orders list drill-down from dashboard metric cards (embedded app: always include shop + host).
 */
export function buildOrdersDrilldownUrl(
  shop: string,
  host: string | null | undefined,
  dateFrom: string,
  dateTo: string,
  segment: DashboardOrderSegment
): string {
  const p = new URLSearchParams()
  appendShopHost(p, shop, host)
  p.set('page', '1')
  p.set('createdAtMin', dateFrom)
  p.set('createdAtMax', dateTo)
  p.set('dashboardSegment', segment)
  return `/orders?${p.toString()}`
}

/** All open orders on the shop (no placement-date filter). */
export function buildOrdersPendingShopWideUrl(
  shop: string,
  host: string | null | undefined
): string {
  const p = new URLSearchParams()
  appendShopHost(p, shop, host)
  p.set('page', '1')
  p.set('dashboardSegment', 'pending')
  return `/orders?${p.toString()}`
}

/** All in-transit orders on the shop (no placement-date filter). */
export function buildOrdersInTransitShopWideUrl(
  shop: string,
  host: string | null | undefined
): string {
  const p = new URLSearchParams()
  appendShopHost(p, shop, host)
  p.set('page', '1')
  p.set('dashboardSegment', 'in_transit')
  return `/orders?${p.toString()}`
}

/** Omit `category` to load all Expense rows in range (not the same as dashboard composite Total Expenses). */
export function buildExpensesDrilldownUrl(
  shop: string,
  host: string | null | undefined,
  dateFrom: string,
  dateTo: string,
  category?: ExpenseDrilldownCategory
): string {
  const p = new URLSearchParams()
  appendShopHost(p, shop, host)
  p.set('from', dateFrom)
  p.set('to', dateTo)
  if (category) p.set('category', category)
  return `/expenses?${p.toString()}`
}

/** Revenue details drill-down (segment totals align with dashboard rollup semantics). */
export function buildRevenueDrilldownUrl(
  shop: string,
  host: string | null | undefined,
  dateFrom: string,
  dateTo: string,
  segment: RevenueDrilldownSegment
): string {
  const p = new URLSearchParams()
  appendShopHost(p, shop, host)
  p.set('page', '1')
  p.set('createdAtMin', dateFrom)
  p.set('createdAtMax', dateTo)
  p.set('revenueSegment', segment)
  return `/revenue-details?${p.toString()}`
}

/** Finance composite metric breakdown (shop_daily_metrics semantics; verify before wiring dashboard cards). */
export function buildMetricBreakdownUrl(
  shop: string,
  host: string | null | undefined,
  dateFrom: string,
  dateTo: string,
  metric: MetricBreakdownKey
): string {
  const p = new URLSearchParams()
  appendShopHost(p, shop, host)
  p.set('dateFrom', dateFrom)
  p.set('dateTo', dateTo)
  p.set('metric', metric)
  return `/metric-breakdown?${p.toString()}`
}
